import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Form, Input, InputNumber, notification, Popconfirm } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { indexBy } from 'utils/utils2';
import { PlusOutlined } from '@ant-design/icons';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import DeleteButton from 'components/DeleteButton';
import { useFormList } from 'hooks/useFormList';
import DF from 'DisplayFramework';
import ButtonOperation from 'components/ButtonOperation';
import SaveButton from 'components/SaveButtonAction';
import {
  addParassetfvcontextuse,
  deleteParassetfvcontextuse,
  getParassetfvcontextuseByContextid,
  ParassetfvcontextuseRequest,
  ParassetfvcontextuseResponse,
  updateParassetfvcontextuse,
} from 'services/assetconfig';
import ParListSelect from 'components/ParListSelect';
import { isNil } from 'lodash';
import { AuthContext } from 'components/Auth/Auth';

const NEW_ACTION_ID = -1;
export type ParAssetFVUseConditionListProps = {
  parcontextid: number;
  refetch: () => void;
  assetfamily?: string;
};
const ParAssetFVUseConditionList = (props: ParAssetFVUseConditionListProps) => {
  const [form] = useForm();
  const { isInspect } = useContext(AuthContext);
  const { assetfamily } = props;
  const fieldName = 'ParAssetFVUseCondition';

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'fvuseid',
    addAsFirst: true,
  });

  const { data: asset, refetch } = useQuery(
    [
      'getParassetfvcontextuseByContextid',
      { parcontextid: props.parcontextid },
    ],
    () =>
      getParassetfvcontextuseByContextid({
        parcontextid: Number(props.parcontextid),
      }).then((res) => {
        return res.list || [];
      }),
    {
      enabled: !isNil(props.parcontextid) && !isNaN(Number(props.parcontextid)),
    }
  );
  useEffect(() => {
    if (asset) {
      form.setFieldsValue({
        [fieldName]: asset,
      });
    }
  }, [form, asset]);
  const getName = useCallback(
    (name: string) => (record: ParassetfvcontextuseResponse) => {
      const actions = form.getFieldValue(fieldName);
      const index = indexBy(actions, 'fvuseid')[record.fvuseid];
      return [fieldName, index, name];
    },
    [form]
  );
  const onSave = useCallback(
    (record: ParassetfvcontextuseResponse) => {
      const condition: ParassetfvcontextuseResponse[] =
        form.getFieldValue(fieldName);
      const index = indexBy(condition, 'fvuseid')[record.fvuseid];
      const currentValue = {
        ...condition[index],
      };
      const { fvuseid } = currentValue;

      if (fvuseid === NEW_ACTION_ID) {
        addParassetfvcontextuse({
          request: { ...currentValue, fvuseid: null as any },
        }).then((response) => {
          if (response.code === '200') {
            notification.success({ message: response.message });
          }
          refetch();
        });
      } else {
        return updateParassetfvcontextuse({
          fvuseid: Number(fvuseid),
          request: currentValue,
        }).then((response) => {
          if (response.code === '200') {
            notification.success({
              message: response.message,
            });
            refetch();
          } else {
            notification.error({
              message: response.message,
            });
          }
        });
      }
    },
    [form, refetch]
  );

  const columns = useMemo<DataGridColDef<ParassetfvcontextuseResponse>[]>(
    () => [
      {
        dataIndex: 'fvuseid',
        render: (value, record, index) => {
          return record?.fvuseid === NEW_ACTION_ID ? (
            <Form.Item
              noStyle
              name={getName('fvuseid')(record)}
              rules={[{ required: true }]}
            >
              <Input disabled />
            </Form.Item>
          ) : (
            <Form.Item>{value}</Form.Item>
          );
        },
        width: '30px',
        fixed: true,
      },
      {
        dataIndex: 'attcode',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('attcode')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect parcode="ASSETUSE" parvalueparent={assetfamily} />
          </Form.Item>
        ),
        width: '110px',
      },

      {
        dataIndex: 'usevaluemin',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('usevaluemin')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: '110px',
      },
      {
        dataIndex: 'usevaluemax',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('usevaluemax')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: '110px',
      },

      {
        dataIndex: 'Actions',
        render: (_, record, index) =>
          record?.fvuseid === NEW_ACTION_ID ? (
            <>
              <SaveButton
                comcode="save"
                size="small"
                onClick={() => !isInspect && onSave(record)}
                htmlType="button"
              />

              <Popconfirm
                title="Are you sure to delete this value?"
                onConfirm={() => {
                  remove(record.fvuseid);
                }}
                okText="Yes"
                cancelText="No"
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </>
          ) : (
            <>
              <SaveButton
                comcode="update"
                size="small"
                onClick={() => !isInspect && onSave(record)}
                htmlType="button"
              />

              <Popconfirm
                title="Are you sure to delete this Condition?"
                onConfirm={() => {
                  deleteParassetfvcontextuse({
                    fvuseid: record.fvuseid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </>
          ),
        width: '50px',
        fixed: 'right',
      },
    ],

    [getName, onSave, refetch, remove, isInspect]
  );
  const addRow = () => {
    const row: ParassetfvcontextuseRequest = {
      fvuseid: NEW_ACTION_ID,
      attcode: '',
      parcontextid: props.parcontextid,
      usevaluemax: 0,
      usevaluemin: 0,
    };
    add(row);
  };
  return (
    <DF.Provider pagcode="parassetfvcontextdetail">
      <Form form={form}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const branchs: ParassetfvcontextuseResponse[] =
              getFieldValue(fieldName);
            const isNewItemInTable =
              asset?.find(({ fvuseid }) => fvuseid === NEW_ACTION_ID) !==
              undefined;
            return (
              <DataGrid
                titleComcode="ParAssetFutureValueUseConditionList"
                rowKey="fvuseid"
                columns={columns}
                dataSource={branchs}
                hideFilters
                extraActions={
                  <ButtonOperation
                    comcode="Add"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => !isInspect && addRow()}
                    disabled={isNewItemInTable}
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

export default ParAssetFVUseConditionList;
